
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import { Client } from "@/models/Client";
import { useUpdateClientSessions } from "@/hooks/clients/useUpdateClientSessions";
import { useTranslation } from "react-i18next";
import { useIsMobile, useIsTablet } from "@/hooks/use-mobile";
import ClientSessionsModalContentWrapper from "./ClientSessionsModalContentWrapper";
import SessionHistoryDialog from "./SessionHistoryDialog";
import PaymentHistoryDialog from "./PaymentHistoryDialog";

interface ClientSessionsModalProps {
  client: Client | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ClientSessionsModal: React.FC<ClientSessionsModalProps> = ({
  client,
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isFullScreen = isMobile || isTablet;
  const [sessionData, setSessionData] = useState<{ [key: string]: number }>({});
  const [showHistory, setShowHistory] = useState(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const { mutate: updateSessions, isPending } = useUpdateClientSessions();

  // Initialize session data when client changes or modal opens
  useEffect(() => {
    if (client && client.subscriptions && open) {
      console.log('Initializing session data for client:', client.id);
      const initialData: { [key: string]: number } = {};
      client.subscriptions.forEach(sub => {
        const currentSessions = sub.currentCycleSessionsUsed || 0;
        initialData[sub.id] = currentSessions;
        console.log(`Subscription ${sub.id}: ${currentSessions} sessions used`);
      });
      console.log('Setting initial session data:', initialData);
      setSessionData(initialData);
    }
  }, [client, open]);

  // Reset session data when modal closes
  useEffect(() => {
    if (!open) {
      console.log('Modal closed, clearing session data');
      setSessionData({});
    }
  }, [open]);

  const handleSessionChange = (subscriptionId: string, value?: string) => {
    console.log(`Changing session count for subscription ${subscriptionId} to value:`, value);
    const numValue = value ? Math.max(0, parseInt(value) || 0): undefined;
    console.log(`Changing sessions for subscription ${subscriptionId} from ${sessionData[subscriptionId]} to ${numValue}`);
    setSessionData(prev => ({
      ...prev,
      [subscriptionId]: numValue
    }));
  };

  const handleSave = () => {
    if (!client) {
      console.error('No client selected');
      return;
    }

    console.log('Saving sessions with data:', sessionData);
    console.log('Client subscriptions:', client.subscriptions);

    const activeSubscriptions = client.subscriptions.filter(sub => sub.isActive);
    console.log('Active subscriptions:', activeSubscriptions);

    const updates = activeSubscriptions.map(sub => {
      const newValue = sessionData[sub.id] ?? sub.currentCycleSessionsUsed ?? 0;
      console.log(`Update for subscription ${sub.id}: ${sub.currentCycleSessionsUsed} -> ${newValue}`);
      return {
        subscriptionId: sub.id,
        sessionsUsed: newValue
      };
    });

    console.log('Final updates to send:', updates);

    if (updates.length === 0) {
      console.error('No active subscriptions to update');
      toast.error(t('clients.noActiveSubscriptions'));
      return;
    }

    updateSessions(updates, {
      onSuccess: (results) => {
        console.log('Sessions saved successfully, results:', results);
        toast.success(t('clients.sessionsUpdated'));
        onOpenChange(false);
      },
      onError: (error) => {
        console.error('Failed to save sessions:', error);
        toast.error(`${t('clients.sessionUpdateFailed')}: ${error.message}`);
      }
    });
  };

  const handleResetSessions = () => {
    if (!client) return;

    console.log('Resetting all sessions to 0');
    const resetData: { [key: string]: number } = {};
    client.subscriptions.forEach(sub => {
      resetData[sub.id] = 0;
    });
    setSessionData(resetData);

    const activeSubscriptions = client.subscriptions.filter(sub => sub.isActive);
    const updates = activeSubscriptions.map(sub => ({
      subscriptionId: sub.id,
      sessionsUsed: 0
    }));

    console.log('Reset updates:', updates);

    updateSessions(updates, {
      onSuccess: () => {
        console.log('Sessions reset successfully');
        toast.success(t('clients.sessionsReset'));
      },
      onError: (error) => {
        console.error('Failed to reset sessions:', error);
        toast.error(`${t('clients.sessionResetFailed')}: ${error.message}`);
      }
    });
  };

  const handleViewHistory = () => {
    setShowHistory(true);
  };

  const handleViewPaymentHistory = () => {
    setShowPaymentHistory(true);
  };

  if (!client) return null;

  const content = (
    <ClientSessionsModalContentWrapper
      client={client}
      sessionData={sessionData}
      onSessionChange={handleSessionChange}
      onViewHistory={handleViewHistory}
      onViewPaymentHistory={handleViewPaymentHistory}
      onCancel={() => onOpenChange(false)}
      onSave={handleSave}
      onReset={handleResetSessions}
      isPending={isPending}
    />
  );

  if (isFullScreen) {
    return (
      <>
        <Sheet open={open} onOpenChange={onOpenChange}>
          <SheetContent 
            side="bottom" 
            className={`${isTablet ? 'h-screen w-full rounded-none border-0 p-0 flex flex-col modal-safe-area-aware ios-modal-safe pwa-modal-header-fix' : 'h-[95vh] w-full rounded-t-xl border-0 p-0 flex flex-col modal-safe-area-aware ios-modal-safe pwa-modal-header-fix'}`}
          >
            <SheetHeader className={`${isTablet ? 'p-6 border-b flex-shrink-0' : 'p-4 pb-2 border-b flex-shrink-0 pt-[calc(1.5rem+env(safe-area-inset-top))] pwa-safe-header'}`}>
              <SheetTitle className="text-lg greek-text">
                {client.firstName} {client.lastName}
              </SheetTitle>
            </SheetHeader>
            <div className={`flex-1 ${isTablet ? 'p-6 pb-safe-bottom' : 'p-4'} overflow-hidden`}>
              {content}
            </div>
          </SheetContent>
        </Sheet>

        <SessionHistoryDialog
          open={showHistory}
          onOpenChange={setShowHistory}
          clientId={client.id}
          clientName={`${client.firstName} ${client.lastName}`}
        />

        <PaymentHistoryDialog
          open={showPaymentHistory}
          onOpenChange={setShowPaymentHistory}
          clientId={client.id}
          clientName={`${client.firstName} ${client.lastName}`}
        />
      </>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl w-[90vw] h-[85vh] p-0 overflow-hidden flex flex-col">
          <DialogHeader className="p-6 pb-4 border-b flex-shrink-0">
            <DialogTitle className="text-xl greek-text">
              {client.firstName} {client.lastName}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 p-6 pt-4 overflow-hidden">
            {content}
          </div>
        </DialogContent>
      </Dialog>

      <SessionHistoryDialog
        open={showHistory}
        onOpenChange={setShowHistory}
        clientId={client.id}
        clientName={`${client.firstName} ${client.lastName}`}
      />

      <PaymentHistoryDialog
        open={showPaymentHistory}
        onOpenChange={setShowPaymentHistory}
        clientId={client.id}
        clientName={`${client.firstName} ${client.lastName}`}
      />
    </>
  );
};

export default ClientSessionsModal;
